/**
 * Accept Invite Page - Accept a share invitation
 *
 * Handles the /invite/:code route to accept monitoring invitations.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  relationshipService,
  type InviteValidation,
} from "../services/relationships";
import { authService, type UserRole } from "../services/auth";
import { Router, type RouterLocation } from "@vaadin/router";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/radio-button/radio-button.js";
import "@shoelace-style/shoelace/dist/components/radio-group/radio-group.js";

@customElement("accept-invite-page")
export class AcceptInvitePage extends LitElement {
  @state() private code = "";
  @state() private isLoading = true;
  @state() private isAccepting = false;
  @state() private validation: InviteValidation | null = null;
  @state() private error = "";
  @state() private success = false;
  @state() private chosenRole: "monitor" | "clinician" = "monitor";

  static styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      padding: 1rem;
      background: #f0f4f8;
    }

    .card {
      max-width: 450px;
      width: 100%;
    }

    .card-content {
      padding: 1.5rem;
    }

    h1 {
      margin: 0 0 1rem 0;
      font-size: 1.25rem;
      text-align: center;
    }

    .description {
      color: #6c757d;
      text-align: center;
      margin-bottom: 1.5rem;
    }

    .info-box {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1.5rem;
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0;
    }

    .info-row:not(:last-child) {
      border-bottom: 1px solid #e9ecef;
    }

    .info-label {
      color: #6c757d;
      font-size: 0.875rem;
    }

    .info-value {
      font-weight: 500;
    }

    .roles {
      display: flex;
      gap: 0.25rem;
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .spinner-container {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }

    .error-container,
    .success-container {
      text-align: center;
    }

    .success-icon {
      font-size: 3rem;
      color: #28a745;
      margin-bottom: 1rem;
    }

    .error-icon {
      font-size: 3rem;
      color: #dc3545;
      margin-bottom: 1rem;
    }

    .login-prompt {
      background: #fff3cd;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
      text-align: center;
    }
  `;

  // Router lifecycle hook to get the invite code
  onAfterEnter(location: RouterLocation) {
    this.code = location.params.code as string;
    this.validateInvitation();
  }

  private async validateInvitation() {
    this.isLoading = true;
    this.error = "";

    try {
      this.validation = await relationshipService.validateInvitation(this.code);
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Invalid invitation";
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return html`
      <sl-card class="card">
        <div class="card-content">
          ${this.isLoading
            ? this.renderLoading()
            : this.success
              ? this.renderSuccess()
              : this.error && !this.validation
                ? this.renderError()
                : this.renderInvitation()}
        </div>
      </sl-card>
    `;
  }

  private renderLoading() {
    return html`
      <div class="spinner-container">
        <sl-spinner style="font-size: 2rem;"></sl-spinner>
      </div>
    `;
  }

  private renderError() {
    return html`
      <div class="error-container">
        <sl-icon class="error-icon" name="exclamation-circle"></sl-icon>
        <h1>Invalid Invitation</h1>
        <p class="description">${this.error}</p>
        <sl-button variant="primary" @click=${() => Router.go("/")}>
          Go to Home
        </sl-button>
      </div>
    `;
  }

  private renderSuccess() {
    return html`
      <div class="success-container">
        <sl-icon class="success-icon" name="check-circle"></sl-icon>
        <h1>You're Connected!</h1>
        <p class="description">
          You can now view ${this.validation?.diabeticDisplayName}'s glucose
          data.
        </p>
        <sl-button variant="primary" @click=${() => Router.go("/monitoring")}>
          View Dashboard
        </sl-button>
      </div>
    `;
  }

  private renderInvitation() {
    const authState = authService.state;
    const isLoggedIn = authState.isAuthenticated;

    // Check if user has an allowed role
    const userRoles = authState.roles || [];
    const allowedRoles = this.validation?.allowedRoles || [];
    const matchingRoles = allowedRoles.filter((role) =>
      userRoles.includes(role as UserRole),
    );
    const hasAllowedRole = matchingRoles.length > 0;
    const needsRole = isLoggedIn && !hasAllowedRole;

    // Auto-select first matching role
    if (hasAllowedRole && !matchingRoles.includes(this.chosenRole)) {
      this.chosenRole = matchingRoles[0] as "monitor" | "clinician";
    }

    return html`
      <h1>
        <sl-icon name="share" style="vertical-align: middle;"></sl-icon>
        Data Sharing Invitation
      </h1>

      <p class="description">
        ${this.validation?.diabeticDisplayName} has invited you to monitor their
        glucose data.
      </p>

      <div class="info-box">
        <div class="info-row">
          <span class="info-label">From</span>
          <span class="info-value"
            >${this.validation?.diabeticDisplayName}</span
          >
        </div>
        <div class="info-row">
          <span class="info-label">Accepted as</span>
          <div class="roles">
            ${this.validation?.allowedRoles.map(
              (role) =>
                html`<sl-badge
                  variant="${role === "monitor" ? "primary" : "neutral"}"
                >
                  ${role === "monitor"
                    ? "Friend/Family"
                    : "Healthcare Professional"}
                </sl-badge>`,
            )}
          </div>
        </div>
      </div>

      ${this.error
        ? html`<sl-alert variant="danger" open style="margin-bottom: 1rem;">
            ${this.error}
          </sl-alert>`
        : ""}
      ${!isLoggedIn
        ? html`
            <div class="login-prompt">
              <sl-icon name="info-circle"></sl-icon>
              You need to log in or create an account to accept this invitation.
            </div>
            <div class="actions">
              <sl-button
                variant="primary"
                @click=${() => this.redirectToLogin()}
              >
                Log In / Sign Up
              </sl-button>
              <sl-button variant="text" @click=${() => Router.go("/")}>
                Cancel
              </sl-button>
            </div>
          `
        : needsRole
          ? html`
              <sl-alert variant="warning" open style="margin-bottom: 1rem;">
                Your account doesn't have the required role
                (${allowedRoles.join(" or ")}) to accept this invitation. Please
                contact ${this.validation?.diabeticDisplayName} for a new
                invitation.
              </sl-alert>
              <sl-button @click=${() => Router.go("/")}> Go to Home </sl-button>
            `
          : html`
              ${matchingRoles.length > 1
                ? html`
                    <div class="role-selection" style="margin-bottom: 1rem;">
                      <label
                        style="display: block; margin-bottom: 0.5rem; font-weight: 500;"
                        >Accept as:</label
                      >
                      ${matchingRoles.map(
                        (role) => html`
                          <sl-radio-button
                            name="role"
                            value=${role}
                            ?checked=${this.chosenRole === role}
                            @click=${() =>
                              (this.chosenRole = role as
                                | "monitor"
                                | "clinician")}
                          >
                            ${role === "monitor"
                              ? "Friend/Family"
                              : "Healthcare Professional"}
                          </sl-radio-button>
                        `,
                      )}
                    </div>
                  `
                : ""}
              <div class="actions">
                <sl-button
                  variant="primary"
                  ?loading=${this.isAccepting}
                  @click=${this.acceptInvitation}
                >
                  <sl-icon slot="prefix" name="check-circle"></sl-icon>
                  Accept Invitation
                </sl-button>
                <sl-button variant="text" @click=${() => Router.go("/")}>
                  Decline
                </sl-button>
              </div>
            `}
    `;
  }

  private redirectToLogin() {
    // Store the invite code to return after login
    sessionStorage.setItem("pendingInvite", this.code);
    Router.go("/login");
  }

  private async acceptInvitation() {
    this.isAccepting = true;
    this.error = "";

    try {
      await relationshipService.acceptInvitation(this.code, this.chosenRole);
      this.success = true;
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to accept invitation";
    } finally {
      this.isAccepting = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "accept-invite-page": AcceptInvitePage;
  }
}
